/**
 * World/session domain types for CP21 gameplay. Kept separate from
 * `src/contract/types.ts` (the CP14 data-contract shape) on purpose: the
 * contract describes the aggregate feed the game *reads*; these types
 * describe the local, per-device persistent world state the game *writes*
 * (Brief §9 "permanent, progressive persistence" - distinct from the
 * season-scoped Tier 2 scoring economy, which resets and is NOT modeled
 * here).
 */

/** Beornings chop right; Lothwen plant left (Brief §2/§9). */
export type Side = 'beornings' | 'lothwen';

/** What a side's action verb is - drives sprite/animation selection. */
export type ActionType = 'chop' | 'plant';

export function actionTypeForSide(side: Side): ActionType {
  return side === 'beornings' ? 'chop' : 'plant';
}

/** One resolved tile (a felled tree or a filled planting plot). */
export interface WorldAction {
  /** 1-based distance from Minas Tirith (world position 0) on this side. */
  pos: number;
  type: ActionType;
  /** ISO-8601 timestamp. */
  at: string;
}

/** Per-side persistent world state - CP21's local stand-in for a future v001.1 server-synced shape. */
export interface WorldSideState {
  side: Side;
  /** Highest resolved tile index on this side; the next tile to act on is frontierPosition + 1. */
  frontierPosition: number;
  actions: WorldAction[];
}

export type PersistedWorldState = Record<Side, WorldSideState>;

/** Dev-harness-controlled session inputs (CP21 §"Interfaces" - no live core-app wiring in v1.0). */
export interface SessionConfig {
  side: Side;
  /** Actions available this session ("3 trees available to chop/plant" - Brief §9, no timer). */
  quota: number;
  /** Harness override standing in for "avg HR vs MAF ceiling" (Brief §2/§9 lore hook). */
  hrOverCeiling: boolean;
  /** Requested fixture filename/path fragment, or null to use the default. */
  fixturePath: string | null;
}
